import { useCallback } from 'react';

import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { useEmailConnectedAccounts, useRestrictActionPopup } from 'src/services';
import { useCustomerActions, useIsCurrentUserAdmin } from 'src/store';
import { useAccountExtractionLoaderPopup } from 'src/views/AccountExtractionLoaderPopup';
import { useAddFileResourcePopup } from 'src/views/AddFileResourcePopup';
import { useBlogCustomModelPopup } from 'src/views/BlogCustomModelPopup';
import { useUpgradePopup } from 'src/views/UpgradePopup';

export const useHandlers = ({
  brandVoiceId,
  isTrainingModel,
  onSubmit,
  onClose,
  onConnectSuccess,
}) => {
  const openAddFileResourcePopup = useAddFileResourcePopup();
  const openAccountExtractionLoaderPopup = useAccountExtractionLoaderPopup();
  const openBlogCustomModelPopup = useBlogCustomModelPopup();
  const emailConnectedAccounts = useEmailConnectedAccounts();
  const customerActions = useCustomerActions();
  const openUpgradePopup = useUpgradePopup();
  const openRestrictActionPopup = useRestrictActionPopup();
  const isCurrentUserAdmin = useIsCurrentUserAdmin();

  const handleChannelClick = useCallback(
    (channel) => {
      onSubmit(channel);
    },
    [onSubmit]
  );

  const handleBlogPostsArticlesClick = useCallback(async () => {
    const result = await openBlogCustomModelPopup({ brandVoiceId, onConnectSuccess });
    if (!result) {
      return;
    }

    openAccountExtractionLoaderPopup({
      customModelAccount: result,
      isTrainingModel: true,
      isAlreadyConnected: true,
    });
    onClose();
  }, [
    openBlogCustomModelPopup,
    brandVoiceId,
    onClose,
    openAccountExtractionLoaderPopup,
    onConnectSuccess,
  ]);

  const handleResourceClick = useCallback(
    async (type) => {
      if (emailConnectedAccounts.length && isTrainingModel) {
        onSubmit(type);
        return;
      }

      // Check user access and customer limits
      if (!isCurrentUserAdmin) {
        openRestrictActionPopup();
        return;
      }
      if (customerActions.featureLimitExceeded(FEATURE_LIMITS.resourcesRowsPerCustomer)) {
        openUpgradePopup(FEATURE_LIMITS.resourcesRowsPerCustomer);
        return;
      }

      const onCreateSuccess = (account) => {
        openAccountExtractionLoaderPopup({
          account,
          isTrainingModel,
        });
      };

      openAddFileResourcePopup({
        type,
        brandVoiceId,
        onCreateSuccess,
        onConnectSuccess,
      });

      onClose();
    },
    [
      emailConnectedAccounts.length,
      isTrainingModel,
      onClose,
      onSubmit,
      isCurrentUserAdmin,
      customerActions,
      openAddFileResourcePopup,
      brandVoiceId,
      onConnectSuccess,
      openRestrictActionPopup,
      openUpgradePopup,
      openAccountExtractionLoaderPopup,
    ]
  );

  return {
    handleChannelClick,
    handleBlogPostsArticlesClick,
    handleResourceClick,
  };
};
